import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.2em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.am-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.am-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.am-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.am-section { text-align: center !important; }
.am-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.am-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.am-icon-orange { background: rgba(232,73,28,0.15); }
.am-icon-green { background: rgba(34,197,94,0.15); }
.am-icon-blue { background: rgba(59,130,246,0.15); }
.am-icon-amber { background: rgba(245,158,11,0.15); }

.am-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.am-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.am-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.am-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.am-card p, .am-card li { font-size: 0.62em; }

.am-three-paths { display: flex; gap: 16px; margin-top: 0.8em; }
.am-path { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 20px; text-align: center; }
.am-path .path-icon { font-size: 1.6em; margin-bottom: 8px; }
.am-path .path-title { font-size: 0.75em; font-weight: 600; color: #e8e8f0; margin-bottom: 8px; }
.am-path .path-detail { font-size: 0.55em; color: #9898b0; line-height: 1.5; }
.am-path.recommended { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.am-path.recommended .path-title { color: #E8491C; }

.am-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.am-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.am-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.am-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.am-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.am-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.am-price-card .detail { font-size: 0.55em; color: #9898b0; }

.am-big-stat { font-size: 1.875em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.am-deadline { display: inline-block; background: rgba(232,73,28,0.15); color: #E8491C; font-size: 0.5em; font-weight: 600; padding: 4px 14px; border-radius: 20px; letter-spacing: 0.05em; margin-bottom: 0.6em; }

.am-compact li { padding: 0.15em 0 0.15em 1.4em !important; }
.am-compact li::before { top: 0.55em !important; }
`;

// Derived pricing for slides (5-10 person team, monthly)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (10 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (10 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();

const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const arcgisMigrationPresentation: SlideDeck = {
	slug: 'livewire-arcgis-migration',
	name: 'ArcGIS Migration',
	subtitle: 'Open source GIS meets cloud-based power delivery modeling',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:0.5em;">
					The Smart Migration Path
				</p>
				<p style="font-size:0.8em;color:#9898b0;margin-bottom:1.5em;">
					Open source GIS meets cloud-based power delivery modeling.
				</p>
				<div class="fragment" style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 2 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-section">
					<span class="am-section-num">01</span>
					<div class="am-deadline">ArcGIS Desktop — End of Support: March 1, 2026</div>
					<h2 style="font-size:2.2em;">The Clock is Ticking</h2>
					<p style="max-width:600px;margin:0 auto;">What the ArcGIS Desktop end-of-life means for your utility</p>
				</div>`,
		},
		// 3 — What's changing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-accent"></div>
				<h2>What's changing</h2>
				<ul>
					<li class="fragment"><strong style="color:#e8e8f0;">Support ends March 1, 2026</strong> — no security updates, no patches, no technical assistance after that date</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Concurrent Use licenses deprecated</strong> — must transition to Named User model. More licenses, higher cost</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Geometric Networks are gone</strong> — ArcGIS Pro requires migration to the Utility Network model. Different data structure, new workflows, extensive retraining</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Hardware upgrades required</strong> — ArcGIS Pro demands significantly more compute power than ArcMap</li>
				</ul>`,
		},
		// 4 — The real risk
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-accent"></div>
				<h2>Staying on ArcMap isn't "doing nothing"</h2>
				<ul>
					<li class="fragment">No security patches — every month increases your exposure</li>
					<li class="fragment">Compliance and audit concerns — running unsupported software</li>
					<li class="fragment">Growing technical debt — new staff won't know ArcMap</li>
					<li class="fragment">Integration decay — other systems move forward while your GIS stands still</li>
				</ul>
				<div class="am-bq fragment" style="margin-top:1em;">
					<p>Doing nothing is the most expensive option. You just don't see the bill until something breaks.</p>
				</div>`,
		},
		// 5 — Three paths forward
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-accent"></div>
				<h2>Three paths forward</h2>
				<div class="am-three-paths">
					<div class="am-path">
						<div class="path-icon">💰</div>
						<div class="path-title">Migrate to ArcGIS Pro</div>
						<div class="path-detail">
							US$500k+ investment<br>
							2+ year timeline<br>
							Named User licensing<br>
							Utility Network migration<br>
							Hardware upgrades<br>
							Extensive retraining
						</div>
					</div>
					<div class="am-path">
						<div class="path-icon">⚠️</div>
						<div class="path-title">Stay on ArcMap</div>
						<div class="path-detail">
							No support after March 2026<br>
							No security updates<br>
							Compliance risk<br>
							Growing technical debt<br>
							Increasing fragility
						</div>
					</div>
					<div class="am-path recommended">
						<div class="path-icon">✓</div>
						<div class="path-title">Open Source + Livewire</div>
						<div class="path-detail">
							Own your infrastructure<br>
							Control your timeline<br>
							Modern cloud platform<br>
							Lower total cost<br>
							Gradual migration<br>
							No vendor lock-in
						</div>
					</div>
				</div>`,
		},
		// 6 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-section">
					<span class="am-section-num">02</span>
					<h2 style="font-size:2.2em;">The Open Source + <span style="font-family:lvwr-ubuntu-thin,sans-serif;color:#E8491C;">Livewire</span> Path</h2>
					<p style="max-width:600px;margin:0 auto;">Replace vendor dependency with ownership and modern tooling</p>
				</div>`,
		},
		// 7 — The approach
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-accent"></div>
				<h2>A two-layer approach</h2>
				<div class="am-card-row">
					<div class="am-card">
						<h3>🗺️ Open Source GIS</h3>
						<p>QGIS for spatial editing, cartography, and data management. Free, mature, and actively developed. Your data stays in open formats — no proprietary lock-in.</p>
					</div>
					<div class="am-card" style="border-color:rgba(232,73,28,0.2);">
						<h3 style="color:#E8491C;">⚡ Livewire</h3>
						<p>Cloud-based power delivery modeling. Network analysis, reliability metrics, what-if scenarios — purpose-built for electric utilities. No desktop install required.</p>
					</div>
				</div>
				<div class="am-bq fragment" style="margin-top:1em;">
					<p>Open source for general GIS. Livewire for power-specific intelligence. Each does what it does best.</p>
				</div>`,
		},
		// 8 — Section 03
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-section">
					<span class="am-section-num">03</span>
					<h2 style="font-size:2.2em;">What You Get</h2>
					<p style="max-width:600px;margin:0 auto;">Capabilities that ArcMap never had</p>
				</div>`,
		},
		// 9 — Cloud Power Modeling
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-icon am-icon-orange">⚡</div>
				<h3>Cloud Power Modeling</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Network analysis and what-if scenarios from any browser. No desktop software, no GIS licenses.
				</p>
				<ul>
					<li class="fragment">Trace connectivity, analyze load distribution, model switching operations</li>
					<li class="fragment">Test capital projects before committing budget — see the impact of every change</li>
					<li class="fragment">Access from the office, the field, or home — just a browser</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">With ArcMap:</strong> desktop-bound, single-user, version-locked<br>
					<strong style="color:#E8491C;">With Livewire:</strong> cloud-native, collaborative, always current
				</p>`,
		},
		// 10 — Reliability Metrics
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-icon am-icon-blue">📊</div>
				<h3>Reliability Metrics</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and other indices for any network configuration — current or hypothetical.
				</p>
				<ul>
					<li class="fragment">Derive reliability indices directly from network topology</li>
					<li class="fragment">Break down performance by feeder, zone, or any spatial boundary</li>
					<li class="fragment">Compare scenarios — what does adding a recloser do to SAIDI?</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">This isn't a GIS feature you're losing.</strong> It's a power delivery feature ArcMap never had.
				</p>`,
		},
		// 11 — Network Versioning
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-icon am-icon-green">📸</div>
				<h3>Network Versioning</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Snapshot and duplicate networks for parallel analysis. Compare any two points in time.
				</p>
				<ul>
					<li class="fragment">Capture the network state before and after major projects</li>
					<li class="fragment">Create what-if copies — model changes without touching production data</li>
					<li class="fragment">Audit-ready documentation — timestamped, immutable, verifiable</li>
				</ul>
				<div class="am-bq fragment" style="margin-top:0.8em;">
					<p>ArcMap versioning was about edit conflicts. Livewire versioning is about understanding change.</p>
				</div>`,
		},
		// 12 — The migration itself
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-accent"></div>
				<h2>A gradual migration — not a big bang</h2>
				<ul>
					<li class="fragment"><strong style="color:#e8e8f0;">Phase 1: Import</strong> — bring your existing network data into Livewire. We handle the data migration</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Phase 2: Parallel run</strong> — use Livewire alongside your current tools. No cold-turkey cutover</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Phase 3: Transition</strong> — shift primary workflows to Livewire + QGIS as comfort grows</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Phase 4: Independence</strong> — your data, your tools, no vendor holding the keys</li>
				</ul>
				<p class="fragment" style="margin-top:1em;font-size:0.72em;text-align:center;color:#E8491C;font-weight:600;">
					Two months to go-live. Not two years.
				</p>`,
		},
		// 13 — Demo
		{
			title: '',
			background: '#0f0f1e',
			content: `
				<div class="am-section">
					<span class="am-section-num">04</span>
					<div style="display:inline-block;background:#E8491C;color:white;font-size:0.5em;font-weight:600;padding:4px 14px;border-radius:20px;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.6em;">Live Demo</div>
					<h2 style="font-size:2.2em;">See the Alternative</h2>
					<p style="max-width:600px;margin:0 auto;">What your workflows look like after the migration</p>
				</div>`,
		},
		// Closing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="am-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">Ready to take control of your GIS future?</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
			verticalSlides: [
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="am-accent"></div>
						<h2>Simple, accessible pricing</h2>
						<div class="am-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
							<p class="card-q">Starting price</p>
							<div class="am-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
							<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
						</div>
						<div class="am-card-row" style="margin-top:16px;">
							<div class="am-card" style="text-align:center;">
								<p class="card-q">Monthly</p>
								<div class="am-big-stat">$${BAND1_MONTHLY}</div>
								<p>per user / month</p>
							</div>
							<div class="am-card" style="text-align:center;">
								<p class="card-q">Annual commitment</p>
								<div class="am-big-stat">$${BAND1_ANNUAL}</div>
								<p>per user / month · 10+ users</p>
							</div>
						</div>
						<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
							100 GB storage included · No GIS licensing required · Browser-based
						</p>`,
				},
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="am-accent"></div>
						<h2>For a 5 to 10 person team</h2>
						<div class="am-card-row">
							<div class="am-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="am-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="am-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="am-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart — 2 months to go-live</p>
							</div>
						</div>
						<div class="am-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="am-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>A fraction of an ArcGIS Pro migration — and you get power delivery modeling too</p>
						</div>`,
				},
			],
		},
	],
};
